"use client";

import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

interface Article {
  id: string | number;
  slug: string;
  titre: string;
  extrait: string;
  categorie: string;
  datePublication: string;
  tempsLecture: string;
  imageUrl: string;
  imageAlt: string;
  auteurNom: string;
  auteurRole: string;
  auteurPhotoUrl: string;
  auteurPhotoAlt: string;
}

interface Props {
  categories: string[];
  articles: Article[];
  categorieColors: Record<string, { color: string; bg: string }>;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-BE', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function ActualitesFilter({ categories, articles, categorieColors }: Props) {
  const [activeCategory, setActiveCategory] = useState('Tous');

  const filtered = activeCategory === 'Tous'
    ? articles
    : articles.filter((a) => a.categorie === activeCategory);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <>
      {/* Category Filter */}
      <section className="bg-white border-b border-gray-100 sticky top-16 md:top-20 z-30">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 overflow-x-auto py-4 scrollbar-hide">
            {categories.map((cat) =>
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium cursor-pointer transition-all ${cat === activeCategory ? 'bg-primary text-white' : 'bg-gray-100 text-text-muted hover:bg-primary/10 hover:text-primary'}`}
                style={{ fontFamily: 'Poppins, sans-serif' }}>
                {cat}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-text-muted" style={{ fontFamily: 'Poppins, sans-serif' }}>Aucun article dans cette catégorie.</p>
            </div>
          )}

          {/* Featured article */}
          {featured && (
            <div className="mb-12">
              <Link
                href={`/actualites/${featured.slug}`}
                className="group grid grid-cols-1 lg:grid-cols-2 gap-0 bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
                aria-label={`Lire l'article : ${featured.titre}`}>

                <div className="relative h-64 lg:h-auto overflow-hidden">
                  <AppImage
                    src={featured.imageUrl}
                    alt={featured.imageAlt}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-500"
                    sizes="(max-width: 1024px) 100vw, 50vw" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" aria-hidden="true" />
                </div>
                <div className="p-8 lg:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${(categorieColors[featured.categorie] ?? { bg: 'bg-blue-50', color: 'text-blue-700' }).bg} ${(categorieColors[featured.categorie] ?? { bg: 'bg-blue-50', color: 'text-blue-700' }).color}`} style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {featured.categorie}
                    </span>
                    {featured.tempsLecture && <span className="text-xs text-text-muted" style={{ fontFamily: 'Poppins, sans-serif' }}>{featured.tempsLecture} de lecture</span>}
                  </div>
                  <h2 className="text-2xl font-bold text-text-main mb-3 group-hover:text-primary transition-colors leading-snug" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {featured.titre}
                  </h2>
                  <p className="text-text-muted text-sm leading-relaxed mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {featured.extrait}
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="relative w-9 h-9 rounded-full overflow-hidden flex-shrink-0 bg-gray-200">
                      <AppImage src={featured.auteurPhotoUrl} alt={featured.auteurPhotoAlt} fill className="object-cover" sizes="36px" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-text-main" style={{ fontFamily: 'Poppins, sans-serif' }}>{featured.auteurNom}</p>
                      <p className="text-xs text-text-muted" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        <time dateTime={featured.datePublication}>{formatDate(featured.datePublication)}</time>
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          )}

          {/* Articles grid */}
          {rest.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {rest.map((article) => {
                const colors = categorieColors[article.categorie] ?? { color: 'text-blue-700', bg: 'bg-blue-50' };
                return (
                  <Link
                    key={article.slug}
                    href={`/actualites/${article.slug}`}
                    className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col"
                    aria-label={`Lire l'article : ${article.titre}`}>

                    <div className="relative h-48 overflow-hidden">
                      <AppImage
                        src={article.imageUrl}
                        alt={article.imageAlt}
                        fill
                        className="object-cover group-hover:scale-105 transition-transform duration-500"
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" aria-hidden="true" />
                      <div className="absolute top-3 left-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${colors.bg} ${colors.color}`} style={{ fontFamily: 'Poppins, sans-serif' }}>
                          {article.categorie}
                        </span>
                      </div>
                    </div>

                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-2 text-xs text-text-muted mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        <time dateTime={article.datePublication}>{formatDate(article.datePublication)}</time>
                        {article.tempsLecture && <><span>·</span><span>{article.tempsLecture} de lecture</span></>}
                      </div>
                      <h3 className="font-bold text-text-main text-base mb-2 leading-snug line-clamp-2 group-hover:text-primary transition-colors" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {article.titre}
                      </h3>
                      <p className="text-text-muted text-sm leading-relaxed line-clamp-3 flex-1 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {article.extrait}
                      </p>
                      <span className="text-primary text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        Lire la suite
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6" /></svg>
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
